import { useCallback, useEffect, useRef, useState } from "react";
import { useBookingFlow } from "../../session/BookingFlowProvider";
import { Field, Notice, Panel } from "../../components/ui";
import { reasonsForStep } from "../FlowMachine";

export function ContactStep() {
  const { projection, send, pending } = useBookingFlow();
  const reasons = reasonsForStep(projection, "contact");
  const contact = projection.contact;
  const [draft, setDraft] = useState({
    firstName: contact?.firstName ?? "",
    lastName: contact?.lastName ?? "",
    email: contact?.email ?? "",
    phone: contact?.phone ?? "",
  });

  function commit() {
    if (!draft.firstName || !draft.lastName || !draft.email) return;
    void send({
      type: "set_contact",
      firstName: draft.firstName,
      lastName: draft.lastName,
      email: draft.email,
      phone: draft.phone || null,
    });
  }

  return (
    <Panel title="Ihre Kontaktdaten" description="Wir schicken die Bestätigung an diese Adresse.">
      <div className="grid-2">
        <Field label="Vorname" htmlFor="firstName" reason={reasons.find((r) => r.field === "firstName")}>
          <input
            id="firstName"
            autoComplete="given-name"
            value={draft.firstName}
            onChange={(event) => setDraft({ ...draft, firstName: event.target.value })}
            onBlur={commit}
          />
        </Field>
        <Field label="Nachname" htmlFor="lastName">
          <input
            id="lastName"
            autoComplete="family-name"
            value={draft.lastName}
            onChange={(event) => setDraft({ ...draft, lastName: event.target.value })}
            onBlur={commit}
          />
        </Field>
      </div>
      <Field label="E-Mail" htmlFor="email" reason={reasons.find((r) => r.field === "email")}>
        <input
          id="email"
          type="email"
          autoComplete="email"
          value={draft.email}
          onChange={(event) => setDraft({ ...draft, email: event.target.value })}
          onBlur={commit}
        />
      </Field>
      <Field label="Telefon (optional)" htmlFor="phone">
        <input
          id="phone"
          type="tel"
          autoComplete="tel"
          value={draft.phone}
          onChange={(event) => setDraft({ ...draft, phone: event.target.value })}
          onBlur={commit}
        />
      </Field>

      <label className="inline-check" htmlFor="business">
        <input
          id="business"
          type="checkbox"
          checked={contact?.isBusinessTrip ?? false}
          disabled={pending}
          onChange={(event) =>
            void send({
              type: "set_business_trip",
              isBusinessTrip: event.target.checked,
              companyName: event.target.checked ? (contact?.companyName ?? "") : null,
            })
          }
        />
        <span>Geschäftsreise — Rechnung an eine Firma</span>
      </label>

      {contact?.isBusinessTrip ? (
        <Field label="Firmenname" htmlFor="company" reason={reasons.find((r) => r.field === "companyName")}>
          <input
            id="company"
            autoComplete="organization"
            defaultValue={contact.companyName ?? ""}
            onBlur={(event) =>
              void send({ type: "set_business_trip", isBusinessTrip: true, companyName: event.target.value })
            }
          />
        </Field>
      ) : null}
    </Panel>
  );
}

export function ReviewStep() {
  const { projection, requestQuote, send, pending } = useBookingFlow();
  const quote = projection.quote;
  const blocking = projection.blockingReasons.filter((r) => r.severity === "blocking");

  if (blocking.length > 0) {
    return (
      <Panel title="Fast fertig" description="Diese Angaben fehlen noch:">
        <ul className="reason-list">
          {blocking.map((reason) => (
            <li key={`${reason.code}-${reason.roomId ?? ""}`}>{reason.message}</li>
          ))}
        </ul>
      </Panel>
    );
  }

  if (!quote) {
    return (
      <Panel title="Preis berechnen" description="Wir ermitteln den verbindlichen Gesamtpreis.">
        <button type="button" className="button" disabled={pending} onClick={() => void requestQuote()}>
          {pending ? "Wird berechnet…" : "Preis anzeigen"}
        </button>
      </Panel>
    );
  }

  return (
    <Panel title="Ihre Buchung" description="Alle Preise inklusive der ausgewiesenen Steuern.">
      {quote.roomLines.map((line, index) => (
        <div className="quote-block" key={line.roomId}>
          <h3>
            {quote.roomLines.length > 1 ? `Zimmer ${index + 1} — ` : ""}
            {line.categoryName}
          </h3>
          <p className="muted">
            {line.rateName === line.mealPlanLabel ? line.rateName : `${line.rateName} · ${line.mealPlanLabel}`}
          </p>
          {line.includedBenefits.length > 0 ? (
            <p className="muted">Enthalten: {line.includedBenefits.join(", ")}</p>
          ) : null}
          <table className="night-table">
            <tbody>
              {line.nights.map((night) => (
                <tr key={night.date}>
                  <th scope="row">{new Date(`${night.date}T00:00:00Z`).toLocaleDateString("de-DE", {
                    weekday: "short",
                    day: "2-digit",
                    month: "2-digit",
                  })}</th>
                  <td>{night.price.formatted}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row">Zwischensumme</th>
                <td>{line.subtotal.formatted}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      ))}

      {quote.addonLines.length > 0 ? (
        <div className="quote-block">
          <h3>Extras</h3>
          <table className="night-table">
            <tbody>
              {quote.addonLines.map((line) => (
                <tr key={line.addonId}>
                  <th scope="row">
                    {line.quantityLabel} {line.displayName}
                  </th>
                  <td>{line.total.formatted}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {quote.taxLines.length > 0 ? (
        <div className="quote-block">
          {quote.taxLines.map((line) => (
            <div className="row-between" key={line.label}>
              <span>
                {line.label}
                {line.note ? <span className="muted"> · {line.note}</span> : null}
              </span>
              <span>{line.total.formatted}</span>
            </div>
          ))}
        </div>
      ) : null}

      <div className="quote-total">
        <div className="row-between total">
          <span>Gesamt</span>
          <strong>{quote.total.formatted}</strong>
        </div>
        <div className="row-between">
          <span>Jetzt fällig</span>
          <span>{quote.dueNow.formatted}</span>
        </div>
        <div className="row-between">
          <span>Bei Anreise</span>
          <span>{quote.dueLater.formatted}</span>
        </div>
        <p className="muted">{quote.paymentSummary}</p>
        <p className="muted">{quote.cancellationSummary}</p>
      </div>

      <button
        type="button"
        className="button"
        disabled={pending}
        onClick={() => void send({ type: "accept_quote", quoteId: quote.quoteId })}
      >
        Angebot annehmen
      </button>
    </Panel>
  );
}

export function PaymentStep() {
  const { projection, checkoutSession, startCheckout, refreshProjection, pending, error } = useBookingFlow();
  const mountRef = useRef<HTMLDivElement | null>(null);
  const checkoutRef = useRef<{ destroy: () => void } | null>(null);
  const requestedRef = useRef(false);
  const [embeddedError, setEmbeddedError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (checkoutSession || requestedRef.current || !projection.quote) return;
    requestedRef.current = true;
    void startCheckout();
  }, [checkoutSession, projection.quote, startCheckout]);

  const confirmPayment = useCallback(async () => {
    setConfirming(true);
    for (let attempt = 0; attempt < 8; attempt += 1) {
      const next = await refreshProjection();
      if (next && ["paid", "payment_authorized", "reservation_committing", "booked"].includes(next.status)) {
        setConfirming(false);
        return;
      }
      await new Promise<void>((resolve) => window.setTimeout(resolve, 1500));
    }
    setConfirming(false);
  }, [refreshProjection]);

  useEffect(() => {
    if (!checkoutSession || !mountRef.current) return;
    const session = checkoutSession;
    let cancelled = false;
    async function mountCheckout() {
      try {
        await loadStripeJs();
        if (cancelled || !window.Stripe || !mountRef.current) return;
        checkoutRef.current?.destroy();
        const stripe = window.Stripe(session.publishableKey);
        const checkout = await stripe.initEmbeddedCheckout({
          clientSecret: session.clientSecret,
          onComplete: () => void confirmPayment(),
        });
        if (cancelled || !mountRef.current) {
          checkout.destroy();
          return;
        }
        checkout.mount(mountRef.current);
        checkoutRef.current = checkout;
      } catch {
        setEmbeddedError("Das sichere Zahlungsformular konnte nicht geladen werden. Bitte versuchen Sie es erneut.");
      }
    }
    void mountCheckout();
    return () => {
      cancelled = true;
      checkoutRef.current?.destroy();
      checkoutRef.current = null;
    };
  }, [checkoutSession, confirmPayment]);

  return (
    <Panel title="Zahlung" description="Sicher über Stripe.">
      {projection.quote ? (
        <div className="quote-total">
          <div className="row-between total">
            <span>Jetzt fällig</span>
            <strong>{projection.quote.dueNow.formatted}</strong>
          </div>
        </div>
      ) : null}
      {checkoutSession?.mode === "sandbox" ? <SandboxPaymentGuide /> : null}
      {pending && !checkoutSession ? <p className="muted" role="status">Sichere Zahlung wird vorbereitet…</p> : null}
      {embeddedError || error ? (
        <Notice tone="warning" title="Zahlung derzeit nicht verfügbar">
          <p>{embeddedError ?? error}</p>
          <button type="button" className="button secondary" onClick={() => {
            requestedRef.current = true;
            setEmbeddedError(null);
            void startCheckout();
          }}>Erneut versuchen</button>
        </Notice>
      ) : null}
      <div ref={mountRef} className="stripe-checkout" aria-busy={pending || confirming} />
      {confirming ? <p className="muted" role="status">Zahlung wird bestätigt…</p> : null}
    </Panel>
  );
}

function SandboxPaymentGuide() {
  return (
    <aside className="sandbox-guide" aria-label="Testkartendaten">
      <div>
        <strong>Sandbox-Zahlung</strong>
        <span>Diese Buchung verwendet keine echte Belastung.</span>
      </div>
      <CopyValue label="Kartennummer" value="4242 4242 4242 4242" />
      <div className="sandbox-guide-grid">
        <CopyValue label="Ablaufdatum" value="02/30" />
        <CopyValue label="Name" value="fourtwo" />
      </div>
      <p className="muted">Eine beliebige dreistellige CVC und eine gültige Test-Postleitzahl verwenden.</p>
    </aside>
  );
}

function CopyValue({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button type="button" className="copy-value" onClick={async () => {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    }}>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{copied ? "Kopiert" : "Zum Kopieren antippen"}</small>
    </button>
  );
}

declare global {
  interface Window {
    Stripe?: (key: string) => {
      initEmbeddedCheckout(options: {
        clientSecret: string;
        onComplete: () => void;
      }): Promise<{ mount(target: HTMLElement): void; destroy(): void }>;
    };
  }
}

function loadStripeJs(): Promise<void> {
  if (window.Stripe) return Promise.resolve();
  const existing = document.querySelector<HTMLScriptElement>("script[data-exodus-stripe]");
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("Stripe.js failed to load")), { once: true });
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://js.stripe.com/v3/";
    script.async = true;
    script.dataset.exodusStripe = "true";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Stripe.js failed to load"));
    document.head.appendChild(script);
  });
}

export function StatusStep() {
  const { projection } = useBookingFlow();
  return (
    <Panel title="Buchungsstatus" description="Diese Seite liest den Status nur — sie bestätigt nichts selbst.">
      <p>Aktueller Stand: {projection.status}</p>
    </Panel>
  );
}
